import { Box, Tabs, Center, Flex } from '@chakra-ui/react';
import { Drawer } from '@halo/ui-react';
import { CloseIcon } from '@halo/ui-chat-react';
import { StateEnum, type User } from '@halo/chat-sdk-react';
import { useActionsStore } from '@/store/actions';
import { EditFormDialog } from '@/components/ProfileEditDialog/EditFormDialog';
import { EditItem } from '@/components/ProfileEditDialog/helpers';
import ProfileBg from './images/defaultBg.png';
import deleteImg from './images/deleteImg.png';
import { FooterActions } from './footerActions';
import { ProfileContent } from './ProfileContent';
import { Header } from './Header';
import { FeedsContent } from './FeedsContent';
import { ShortsContent } from './ShortsContent';

interface ProfileDialogProps {
  user: User;
  isMe: boolean;
  handleUpdateProfileUser: () => void;
  countryLabel: string;
}

export const ProfileDialog = memo(function ProfileDialog({
  user,
  isMe,
  handleUpdateProfileUser,
  countryLabel,
}: ProfileDialogProps) {
  const { profileOpen, profileReadonly: readonly, setProfileOpen } = useActionsStore();
  const [editFormVisible, setEditFormVisible] = useState(false);
  const { t } = useTranslation();
  const isDeleteAccount = useMemo(() => {
    return user?.state === StateEnum.DELETE_ACCOUNT;
  }, [user?.state]);

  return (
    <>
      <Drawer.Root open={profileOpen} onOpenChange={(details) => setProfileOpen(details.open)}>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content w="480px" maxW="100%">
            <Drawer.Header
              className="Header"
              pt="5"
              px="5"
              pb="2.5"
              justifyContent="space-between"
            >
              <Drawer.Title fontSize="md" fontWeight="semibold">
                {t('profile.profileTitle')}
              </Drawer.Title>
              <Center cursor="pointer" onClick={() => setProfileOpen(false)}>
                <CloseIcon />
              </Center>
            </Drawer.Header>
            <Drawer.Body
              className="scrollHover"
              p={0}
              bg={isDeleteAccount ? 'bg.subtle' : 'bg.subtle'}
            >
              <Box
                className="Box"
                w="full"
                bgImage={
                  isDeleteAccount
                    ? 'linear-gradient(180deg, #f8f8f8 0%, #c5c5c5 100%)'
                    : `url(${ProfileBg})`
                }
                bgSize="cover"
                bgPos="center top"
                bgRepeat="no-repeat"
                h="300px"
              ></Box>
              <Header
                user={user}
                isMe={isMe}
                countryLabel={countryLabel}
                setEditFormVisible={setEditFormVisible}
              />
              {!isDeleteAccount ? (
                <Tabs.Root defaultValue="profile" px="5" minH="346px">
                  <Tabs.List border="none" gap="6" px="0" fontSize="sm">
                    <Tabs.Trigger value="profile" _before={{ w: '3', margin: 'auto' }} px="0">
                      {t('profile.profileTitle')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="feeds" _before={{ w: '3', margin: 'auto' }} px="0">
                      {t('profile.feeds')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="shorts" _before={{ w: '3', margin: 'auto' }} px="0">
                      {t('profile.shorts')}
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value="profile">
                    <ProfileContent user={user} countryLabel={countryLabel} />
                  </Tabs.Content>
                  <Tabs.Content value="feeds">
                    <FeedsContent user={user} />
                  </Tabs.Content>
                  <Tabs.Content value="shorts">
                    <ShortsContent user={user} />
                  </Tabs.Content>
                </Tabs.Root>
              ) : (
                <Flex alignItems="center" flexDirection="column" gap="2">
                  <img src={deleteImg} width="170px" height="100px"></img>
                  <Box>{t('profile.accountNoExistsDesc')}</Box>
                </Flex>
              )}
            </Drawer.Body>
            <Drawer.Footer
              overflow="hidden"
              justifyContent="start"
              px="4"
              bg="bg.subtle"
              rounded="xl"
            >
              {!isDeleteAccount && !readonly && <FooterActions user={user} isMe={isMe} />}
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
      <EditFormDialog
        title={`${t('buttons.edit')} ${t('profile.bio')}`}
        item={EditItem.Bio}
        show={editFormVisible}
        setShow={setEditFormVisible}
        handleUpdateProfileUser={handleUpdateProfileUser}
      />
    </>
  );
});
